import { type ComponentProps, useEffect, useLayoutEffect, useRef } from "react"
import cn from "@/v2-ui/utils/utils.cn"

const SHOULD_MINIFY_TRESHOLD = 50 // Threshold for triggering onShouldMinify

type BaseProps = ComponentProps<"div">
export type ResizableProps = BaseProps & {
  handleClassName?: BaseProps["className"]
  direction?: "horizontal" | "vertical"
  position?: "start" | "end"
  /** default: before */
  handlePlacement?: "before" | "after"
  minVal?: number
  maxVal?: number
  /**
   * Imperative size (height when `direction="horizontal"`, width otherwise).
   * Applied via the DOM, not React `style`, so parent re-renders do not fight
   * the live drag. Omit while the user is dragging; update on resize-end.
   */
  size?: number
  minifyMaximizedTreshold?: number
  showGrip?: boolean
  onShouldMinify?: () => void
  onShouldMaxify?: () => void
  onResize?: (val: number) => void
  onResizeEnd?: (val: number) => void
}

function Resizable(props: ResizableProps) {
  const {
    className,
    handleClassName,
    direction = "horizontal",
    position = "end",
    handlePlacement = "before",
    minVal = Number.NEGATIVE_INFINITY,
    maxVal = Number.POSITIVE_INFINITY,
    showGrip = false,
    size,
    minifyMaximizedTreshold = SHOULD_MINIFY_TRESHOLD,
    onShouldMinify,
    onShouldMaxify,
    onResize,
    onResizeEnd,
    children,
    ...divProps
  } = props

  const positionRef = useRef<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const isHorizontal = direction === "horizontal"
  const excessBeyondMinRef = useRef(0)
  const excessBeyondMaxRef = useRef(0)
  const currentPosition = useRef<number | null>(null)
  const onResizeRef = useRef(onResize)
  const onResizeEndRef = useRef(onResizeEnd)
  const onShouldMinifyRef = useRef(onShouldMinify)
  const onShouldMaxifyRef = useRef(onShouldMaxify)
  onResizeRef.current = onResize
  onResizeEndRef.current = onResizeEnd
  onShouldMinifyRef.current = onShouldMinify
  onShouldMaxifyRef.current = onShouldMaxify

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || size == null) return
    if (isHorizontal) container.style.height = `${size}px`
    else container.style.width = `${size}px`
  }, [size, isHorizontal])

  useEffect(() => {
    function getQ(e: MouseEvent) {
      return isHorizontal ? e.y : e.x
    }

    const resize = (e: MouseEvent) => {
      e.preventDefault()
      const q = getQ(e)
      const dx = positionRef.current - q
      positionRef.current = q
      const container = containerRef.current
      if (!container) return
      if (isHorizontal) {
        const calcHeight =
          position === "start"
            ? container.clientHeight - dx
            : container.clientHeight + dx

        // Check parent's max-height constraint
        let effectiveMax = maxVal
        const parent = container.parentElement
        if (parent) {
          const parentStyle = getComputedStyle(parent)
          const parentMaxHeight = Number.parseFloat(parentStyle.maxHeight)
          if (
            !Number.isNaN(parentMaxHeight) &&
            parentMaxHeight < effectiveMax
          ) {
            effectiveMax = parentMaxHeight
          }
        }

        const newHeight = Math.min(Math.max(calcHeight, minVal), effectiveMax)
        container.style.height = `${newHeight}px`
        currentPosition.current = newHeight
        onResizeRef.current?.(newHeight)

        if (onShouldMinifyRef.current) {
          if (newHeight <= minVal) {
            excessBeyondMinRef.current -= dx
          }
          if (Math.abs(excessBeyondMinRef.current) >= minifyMaximizedTreshold) {
            onShouldMinifyRef.current()
            excessBeyondMinRef.current = 0
          }
        }

        if (onShouldMaxifyRef.current) {
          if (newHeight >= effectiveMax) {
            excessBeyondMaxRef.current += dx
          }
          if (Math.abs(excessBeyondMaxRef.current) >= minifyMaximizedTreshold) {
            onShouldMaxifyRef.current()
            excessBeyondMaxRef.current = 0
          }
        }
      } else {
        const calcWidth =
          position === "start"
            ? container.clientWidth + dx
            : container.clientWidth - dx

        // Check parent's max-width constraint
        let effectiveMax = maxVal
        const parent = container.parentElement
        if (parent) {
          const parentStyle = getComputedStyle(parent)
          const parentMaxWidth = Number.parseFloat(parentStyle.maxWidth)
          if (!Number.isNaN(parentMaxWidth) && parentMaxWidth < effectiveMax) {
            effectiveMax = parentMaxWidth
          }
        }

        const newWidth = Math.min(Math.max(calcWidth, minVal), effectiveMax)
        container.style.width = `${newWidth}px`
        currentPosition.current = newWidth
        onResizeRef.current?.(newWidth)

        if (onShouldMinifyRef.current) {
          if (newWidth <= minVal) {
            excessBeyondMinRef.current -= dx
          }
          if (Math.abs(excessBeyondMinRef.current) >= minifyMaximizedTreshold) {
            onShouldMinifyRef.current()
            excessBeyondMinRef.current = 0
          }
        }

        if (onShouldMaxifyRef.current) {
          if (newWidth >= effectiveMax) {
            excessBeyondMaxRef.current += dx
          }
          if (Math.abs(excessBeyondMaxRef.current) >= minifyMaximizedTreshold) {
            onShouldMaxifyRef.current()
            excessBeyondMaxRef.current = 0
          }
        }
      }
    }

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      positionRef.current = getQ(e)
      document.addEventListener("mousemove", resize, false)
      document.documentElement.style.cursor = isHorizontal
        ? "ns-resize"
        : "ew-resize"
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", resize, false)
      if (currentPosition.current)
        onResizeEndRef.current?.(currentPosition.current)
      currentPosition.current = null
      document.documentElement.style.cursor = ""
      excessBeyondMinRef.current = 0
      excessBeyondMaxRef.current = 0
    }

    const handle = handleRef.current
    if (handle) {
      handle.addEventListener("mousedown", onMouseDown, false)
    }
    document.addEventListener("mouseup", onMouseUp, false)

    return () => {
      if (handle) {
        handle.removeEventListener("mousedown", onMouseDown, false)
      }
      document.removeEventListener("mouseup", onMouseUp, false)
      document.removeEventListener("mousemove", resize, false)
      excessBeyondMinRef.current = 0
      excessBeyondMaxRef.current = 0
    }
  }, [minifyMaximizedTreshold, minVal, maxVal, isHorizontal, position])

  const handle = (
    <div
      ref={handleRef}
      className={cn("resizable-handle", handleClassName, {
        "resizable-handle__horizontal": isHorizontal,
        "resizable-handle__vertical": !isHorizontal,
        "resizable-handle__start": position === "start",
        "resizable-handle__end": position === "end",
        "resizable-handle--with-grip": showGrip,
      })}
    />
  )

  return (
    <div
      ref={containerRef}
      className={cn("resizable", className)}
      {...divProps}
    >
      {handlePlacement === "before" ? handle : null}
      {children}
      {handlePlacement === "after" ? handle : null}
    </div>
  )
}

export default Resizable
